import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import { DATE_FORMATS, DISCOUNT_TYPES, formatDate, showError } from 'utils';
import styles from './index.module.scss';
import _ from 'lodash';
import { fetchDiscount, updateDiscount } from 'services/api/discounts';
import WholeNumberInput from 'components/WholeNumberInput';
import DecimalInput from 'components/DecimalInput';
import Currency from 'components/Currency/Currency';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import DropDown from 'components/FormFields/dropdown';
import { fetchOrders } from 'services';
import { Orders } from 'pages/orders';

const DiscountDetails = props => {
  const { discountId } = props.match.params;
  const [discount, setDiscount] = useState<Record<string, any>>({ type: '' });
  const [loading, setLoading] = useState<boolean>(false);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<Record<string, any>>({ data: true });
  const [startDate, setStartDate] = useState<string>(moment().toISOString());
  const [endDate, setEndDate] = useState<string>(moment().toISOString());
  const [detailsEditable, setDetailsEditable] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  // We need this otherwise if any property is not present in the discount object and 
  // we will try to edit that then it will give "uncontrolled to controlled input" error
  const defaultStateOfDiscount = {
    userLimit: '',
    globalLimit: '',
    discountAmount: '',
  };

  const fetchDiscountDetailsFn = async () => {
    setLoading(true);
    try {
      const response = await fetchDiscount(discountId);
      setDiscount({ ...defaultStateOfDiscount, ...response });
      response.startDate ?? setStartDate(moment(response.startDate).toISOString());
      response.endDate ?? setEndDate(moment(response.endDate).toISOString());
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscountDetailsFn();
  }, []);

  const updateDiscountDetailsFn = async () => {
    setUpdateLoading(true);
    try {
      await updateDiscount({
        ...discount,
        id: discountId,
        startDate: startDate.toString(),
        endDate: endDate.toString(),
      });
      setDetailsEditable(false);
    } catch (e) {
      showError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Discount Details</div>
      <BackButton />
    </div>
  );

  const handleEdit = () => {
    if (detailsEditable) {
      updateDiscountDetailsFn();
    } else {
      setDetailsEditable(true);
    }
  };

  const getActionButtons = () => (
    <div className='d-flex'>
      <ActionButton
        text={detailsEditable ? 'Save' : 'Edit'}
        active={!updateLoading}
        isLoading={updateLoading}
        onClick={handleEdit}
        customClass={'mb-3'}
      />
      {detailsEditable ?
        <ActionButton
          text={'Cancel'}
          active={!updateLoading}
          isLoading={updateLoading}
          onClick={() => setDetailsEditable(false)}
          customClass={'mb-3 ml-2'}
        />
        : null}
    </div>
  );

  const getPercentOrAmount = () => {
    if ((discount?.type).includes('FIXED')) {
      return (
        <div className={styles['details-group']}>
          <div className={styles['label']}>Discount Amount</div>
          {detailsEditable ? (
            <WholeNumberInput
              value={discount.discountAmount}
              disabled={updateLoading}
              onChange={e => setDiscount({ ...discount, discountAmount: e })}
            />
          ) : (
            <div className={styles['detail']}><Currency amount={discount.discountAmount} /></div>
          )}
        </div>
      );
    } else {
      return (
        <div className={styles['details-group']}>
          <div className={styles['label']}>Discount Percent</div>
          {detailsEditable ? (
            <DecimalInput
              value={discount.discountPercent}
              disabled={updateLoading}
              onChange={e => setDiscount({ ...discount, discountPercent: e })}
            />
          ) : (
            <div className={styles['detail']}>{discount.discountPercent}%</div>
          )}
        </div>
      );
    }
  };

  const getGlobalLimit = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>Global Limit</div>
      {detailsEditable ? (
        <WholeNumberInput
          value={discount.globalLimit}
          disabled={updateLoading}
          onChange={e => setDiscount({ ...discount, globalLimit: e })}
        />
      ) : (
        <div className={styles['detail']}>{discount.globalLimit ?? '-'}</div>
      )}
    </div>
  );

  const getUserLimit = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>User Limit</div>
      {detailsEditable ? (
        <WholeNumberInput
          value={discount.userLimit}
          disabled={updateLoading}
          onChange={val => setDiscount({ ...discount, userLimit: val })}
        />
      ) : (
        <div className={styles['detail']}>{discount.userLimit}</div>
      )}
    </div>
  );

  const getDiscountLimits = () => (
    <>
      {getGlobalLimit()}
      {getUserLimit()}
    </>
  );

  const getStartDate = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>Start Date</div>
      {detailsEditable ? (
        <div className={styles['date-picker']}>
          <DatePicker
            wrapperClassName="w-100"
            className={'form-control'}
            showTimeInput
            dateFormat={DATE_FORMATS.MM_dd_yyyy__EE_hh_mm_a}
            selected={startDate ? moment(startDate).toDate() : null}
            onChange={(e: any) => setStartDate(moment(e).toISOString())}
          />
        </div>
      ) : (
        <div className={styles['detail']}>{formatDate(startDate, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
      )}
    </div>
  );

  const getEndDate = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>End Date</div>
      {detailsEditable ? (
        <div className={styles['date-picker']}>
          <DatePicker
            wrapperClassName="w-100"
            className={'form-control'}
            dateFormat={DATE_FORMATS.MM_dd_yyyy__EE_hh_mm_a}
            showTimeInput
            selected={endDate ? moment(endDate).toDate() : null}
            onChange={(e: any) => setEndDate(moment(e).toISOString())}
          />
        </div>
      ) : (
        <div className={styles['detail']}>{formatDate(endDate, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
      )}
    </div>
  );

  const getDates = () => (
    <>
      {getStartDate()}
      {getEndDate()}
    </>
  );

  const getDiscountDetail = () => (
    <>
      <div className={styles['details-group']}>
        <div className={styles['label']}>Discount ID</div>
        <div className={`${styles['detail']} ${styles['id']}`}>{discount.objectId}</div>
      </div>
      <div className={styles['details-group']}>
        <div className={styles['label']}>Created At</div>
        <div className={styles['detail']}>{formatDate(discount.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
      </div>
    </>
  );

  const getDiscountCode = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>Discount Code</div>
      {detailsEditable ? (
        <input
          className={'form-control'}
          value={discount.code}
          onChange={e => setDiscount({ ...discount, code: e })}
        />
      ) : (
        <div className={styles['detail']}>{discount.code ?? '-'}</div>
      )}
    </div>
  );

  const getDiscountType = () => (
    <div className={styles['details-group']}>
      <div className={styles['label']}>Discount Type</div>
      {detailsEditable ? (
        <DropDown value={discount.type} onChange={(e) => setDiscount({ ...discount, type: e })} options={DISCOUNT_TYPES} />
      ) : (
        <div className={styles['detail']}>{discount.type ?? '-'}</div>
      )}
    </div>
  );

  const getLeftSection = () => (
    <div>
      {getDiscountDetail()}
      {getDiscountLimits()}
      {getDates()}
    </div>
  );

  const getRightSection = () => (
    <div >
      {getActionButtons()}
      {getDiscountCode()}
      {getPercentOrAmount()}
      {getDiscountType()}
    </div>
  );

  const handleOrdersClick = async () => {
    setOrdersLoading(true);
    const filter = { discountCode: discount?.code };
    try {
      const response = await fetchOrders(filter);
      setOrders(response);
    } catch (e) {
      showError(e);
    } finally {
      setOrdersLoading(false);
    }
  };

  const getOrderItems = () => (
    <>
      {orders?.items ?
        <Orders
          {...props}
          data={orders.items}
          total={orders.total}
          selectableRows={false}
          hideFilter={true}
        />
        : null}
    </>
  );

  const getOrders = () => {
    if (!detailsEditable) {
      return (
        <>
          <div className=''>
            <ActionButton
              active={!ordersLoading}
              isLoading={ordersLoading}
              customClass={'m-2'}
              onClick={handleOrdersClick}
              text={'Show Orders'}
            />
          </div>
          {getOrderItems()}
        </>
      );
    } else {
      return null;
    }
  };

  const getBasicDetails = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['details-section']}>
        {getLeftSection()}
        {getRightSection()}
      </div>
    </div >
  );

  if (loading) return <Loading />;

  return (
    <div className={styles['main-container']}>
      {getHeader()}
      {getBasicDetails()}
      {getOrders()}
    </div>
  );
};

export { DiscountDetails };